import React, { useCallback, useEffect, useState } from 'react';
import {
  Box, Button, Checkbox, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogContentText, DialogTitle, FormControlLabel, FormGroup, Tooltip, Typography,
} from '@mui/material';
import InsightsIcon from '@mui/icons-material/Insights';
import PropTypes from 'prop-types';
import { useAuth } from '../context/AuthContext';
import AnalysisDateRange, { toApiDate } from './AnalysisDateRange';

/**
 * Runs the Integral Delta auto-analysis on whatever symbol the chart is showing.
 *
 * <h2>The symbol is the only thing sent</h2>
 *
 * Not the bars. The backend already holds the price history and is the only side that can run the
 * ephemeris, so posting a few thousand bars up so it could compute what it already has would be
 * slower and would introduce a second source of truth for the same series — a chart that had been
 * simulated or had its future bars padded would analyse a market that never existed.
 *
 * <p>Named `.js` rather than `.jsx` to match the other 115 components here. CRA compiles both; a
 * lone `.jsx` is the kind of difference that makes a later grep miss a file.
 *
 * <h2>It reports; the parent draws</h2>
 *
 * The report is handed to `onComplete` and nothing is drawn here. A button that drew straight onto
 * the chart would have to own an overlay id and a clearing rule, and would then be a study rather
 * than an analysis — so the parent owns both, and can also decide to only journal the result.
 *
 * <h2>The click OPENS A DIALOG; it does not run anything</h2>
 *
 * The window the analysis covers lives here, in a modal the button raises, rather than as two
 * pickers parked permanently in the sidebar. A range that is only read at the moment of a run does
 * not need to occupy the layout for the rest of the session, and putting it beside the button that
 * consumes it makes the pair legible as one action: choose a period, run it.
 *
 * <p>It also removes a foot-gun. Sidebar pickers hold their value across symbol changes and chart
 * reloads, so a range set an hour ago silently scoped a run nobody remembered narrowing. A dialog
 * that opens with the last choice still visible is the same convenience without the ambush — the
 * dates are on screen at the moment they take effect.
 *
 * <h2>Empty means the WHOLE stored history</h2>
 *
 * Both bounds are optional and an absent one is OMITTED from the payload, which the backend reads
 * as "as far as the data goes" on that side. The button never defaults them to the loaded chart
 * span: that silently made the same run give different answers depending on the scroll position.
 */
const ChartAnalysisButton = ({ symbol, onComplete, onError, disabled, variant, size }) => {
  const { api, token } = useAuth();
  const [running, setRunning] = useState(false);
  const [open, setOpen] = useState(false);
  // dayjs objects or null. Kept across OPENINGS so a sweep does not mean re-picking the same
  // quarter repeatedly - but CLEARED when the symbol changes, and that is the fix.
  //
  // A range picked while looking at one instrument is not a statement about the next one. Retained
  // across symbols it silently scoped every later run, and the only warning was the button label -
  // which is easy to read past when the number on it has not changed for twenty symbols. The
  // measured symptom was an analysis that stopped years short of the data with nothing on the
  // chart to say why.
  const [range, setRange] = useState({ start: null, end: null });

  useEffect(() => {
    setRange({ start: null, end: null });
  }, [symbol]);

  /**
   * WHICH MECHANICS TO RUN. All three on, which is the same study as before this control existed.
   *
   * <p>Checkboxes rather than radios because the question is "which of these do I want", and the
   * interesting answers are usually more than one — PTA and PTB together, or everything except
   * CTS. A radio group would make the common case unreachable.
   *
   * <p>NOT reset when the symbol changes, unlike the date range. A range is a statement about one
   * instrument's history; "I am studying the pillars" is a statement about the strategy, and
   * re-ticking it for every symbol in a sweep is exactly the friction the reset exists to avoid
   * elsewhere.
   */
  const [signals, setSignals] = useState({ CTS: true, PTA: true, PTB: true });
  const selected = Object.keys(signals).filter(k => signals[k]);

  const run = useCallback(async () => {
    if (!symbol || running) return;
    const startDate = toApiDate(range.start);
    const endDate = toApiDate(range.end);
    // An OPEN end means "to date", and it is sent as absent rather than as today's date on
    // purpose: the backend reads a missing bound as "as far as the data goes", which is the honest
    // answer when the newest bar is three days old. Pinning it to the browser's today would ask
    // for a range the store cannot cover and make the result depend on the clock of whoever ran it.
    console.debug(`[analysis] ${symbol} range `
      + `${startDate || '(all history)'} .. ${endDate || '(to the newest bar)'}`
      + ` signals ${selected.join('+') || '(all)'}`);
    // Closed FIRST, so the chart it is about to redraw is not behind a modal. The request carries
    // the dates it read a moment ago, so closing cannot race it.
    setOpen(false);
    setRunning(true);
    try {
      const res = await api.post('/api/integral-delta/analyse',
        // Only sent when present. The backend refuses a date it cannot read rather than widening
        // the run silently, so an empty string here would be a needless way to trip that.
        // `signals` is omitted when all three are on, so the ordinary request is byte-identical
        // to what it was before this control existed — and the backend reads an absent list as
        // "everything" anyway, so there is nothing to disagree about.
        {
          symbol,
          ...(startDate ? { startDate } : {}),
          ...(endDate ? { endDate } : {}),
          ...(selected.length === 3 ? {} : { signals: selected }),
        },
        { headers: { Authorization: `Bearer ${token}` } });

      const data = res?.data || {};
      if (data.error) {
        if (onError) onError(data.error);
        return;
      }
      // A symbol with no stored history answers with a note rather than a report, and it is not an
      // error: the chart simply has nothing to analyse yet. Treating it as a failure would put a
      // red message on a perfectly healthy chart.
      if (!data.report) {
        if (onError) onError(data.note || 'No analysis was produced for this symbol.');
        return;
      }
      // The DynaMan comparison rides ON the report rather than arriving as a second argument, so
      // every existing consumer of onComplete keeps working unchanged and the journal formatter
      // can reach it without App having to thread a new parameter through.
      if (onComplete) {
        onComplete({
          ...data.report,
          dynaManModes: data.dynaManModes,
          dynaManModesError: data.dynaManModesError,
        });
      }
    } catch (err) {
      const message = err?.response?.data?.error || err?.message || 'Analysis failed.';
      if (onError) onError(message);
    } finally {
      // In a finally, so a thrown request cannot leave the button spinning forever — which on a
      // long analysis looks identical to one that is still working.
      setRunning(false);
    }
  }, [api, token, symbol, range, running, selected, onComplete, onError]);

  const from = toApiDate(range.start);
  const to = toApiDate(range.end);
  const scoped = Boolean(from || to);
  const span = scoped
    ? `${from || 'the start'} to ${to || 'the end'}`
    : 'the whole stored history';
  const title = symbol
    ? (scoped
      ? `Scoped to ${span}. The phase-1 studies only produce intervals that COMPLETE inside the `
        + 'window, so a short one legitimately yields no PTA or PTB levels and therefore no '
        + 'geometry signals. Clear both dates to analyse the whole history.'
      : `Choose a period and run the Integral Delta auto-analysis on ${symbol}: PTA and PTB `
        + 'geometry, CTS exhaustion, and the ATR trade-management machine.')
    : 'Load a symbol first';

  return (
    <>
      <Tooltip arrow placement="bottom" title={running ? 'Analysing…' : title}>
        {/* A disabled button fires no events, so the tooltip needs an element that does. */}
        <span>
          <Button
            variant={variant}
            size={size}
            disabled={disabled || running || !symbol}
            onClick={() => setOpen(true)}
            startIcon={running
              ? <CircularProgress size={16} color="inherit" />
              : <InsightsIcon />}
            sx={{ textTransform: 'none' }}
          >
            {/* A retained range is announced ON THE BUTTON, not only inside the dialog.
                Retention is a real convenience for a sweep and a real trap without this: a window
                set once silently scoped every later run, and a short window produces no completed
                PTA or PTB interval at all - which reads as the geometry having broken rather than
                as the analysis having been narrowed. */}
            {running ? 'Analysing…' : (scoped ? `Auto-Analysis · ${span}` : 'Auto-Analysis')}
          </Button>
        </span>
      </Tooltip>

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm">
        <DialogTitle sx={{ pb: 1 }}>Auto-Analysis — {symbol || 'no symbol'}</DialogTitle>
        <DialogContent>
          <DialogContentText variant="body2" sx={{ mb: 2 }}>
            {/* Named before the run, not discovered after it. The period is the single thing that
                decides what the numbers mean, and it is the one thing a reader cannot infer from
                the result. */}
            Analysing <strong>{span}</strong>. Leave a box empty to reach as far as the data goes
            on that side.
            {scoped && (
              <>
                {' '}A window only contains the PTA and PTB intervals that <strong>complete</strong>
                {' '}inside it — Venus and Mercury take months to cross a sign — so a short range
                {' '}can legitimately produce no geometry levels and no geometry signals at all.
              </>
            )}
          </DialogContentText>
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Signal mechanics</Typography>
            <FormGroup row>
              {['CTS', 'PTA', 'PTB'].map(k => (
                <FormControlLabel
                  key={k}
                  control={(
                    <Checkbox
                      size="small"
                      checked={signals[k]}
                      onChange={e => setSignals(p => ({ ...p, [k]: e.target.checked }))}
                    />
                  )}
                  label={k}
                />
              ))}
            </FormGroup>
            {selected.length === 0 && (
              // UNCHECKING EVERYTHING IS NOT A RUN OF NOTHING. An empty study is
              // indistinguishable on screen from a symbol with no setups, which is the one
              // reading that must never be ambiguous - so the backend treats an empty list as
              // "everything" and this says so before the button is pressed.
              <Typography variant="caption" color="text.secondary">
                Nothing selected — all three will be run.
              </Typography>
            )}
          </Box>

          <AnalysisDateRange
            start={range.start}
            end={range.end}
            onChange={setRange}
            disabled={running}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)} disabled={running}>Cancel</Button>
          <Button
            variant="contained"
            onClick={run}
            disabled={running || !symbol}
            sx={{ textTransform: 'none' }}
          >
            Run Analysis
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

ChartAnalysisButton.propTypes = {
  /** The symbol currently on the chart. The button is disabled without one. */
  symbol: PropTypes.string,
  /** Handed the AnalysisReport on success. */
  onComplete: PropTypes.func,
  /** Handed a human-readable message on failure, or when there is nothing to analyse. */
  onError: PropTypes.func,
  disabled: PropTypes.bool,
  variant: PropTypes.string,
  size: PropTypes.string,
};

ChartAnalysisButton.defaultProps = {
  variant: 'contained',
  size: 'small',
};

export default ChartAnalysisButton;
